import React, { useMemo } from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

/**
 * Renders the SEO content blocks attached to a Tool (`tool.meta.pageCopy`).
 * Supports several block shapes seeded by `backend/scripts/seed/seed_tools.js`:
 *   - 'section'        — title + optional subtitle/copy
 *   - 'ordered-list'   — numbered list of items
 *   - 'unordered-list' — bullet / dash / check list of items
 *   - 'cards'          — 2-up grid of small icon cards
 *   - 'faq'            — accordion-less Q/A list
 */
function ToolPageCopy({ blocks, palette, isMD }) {
  const { colors } = useTheme();

  const styles = useMemo(() => makeStyles(colors, palette, isMD), [colors, palette, isMD]);

  if (!blocks?.length) return null;

  return (
    <View style={styles.wrap}>
      {blocks.map((block, index) => (
        <PageCopyBlock
          key={`page-copy-${index}`}
          block={block}
          palette={palette}
          styles={styles}
        />
      ))}
    </View>
  );
}

function PageCopyBlock({ block, palette, styles }) {
  const type = block.type || 'section';

  return (
    <View style={styles.block}>
      {(block.title || block.icon) && (
        <View style={styles.header}>
          {!!block.icon && (
            <View style={styles.headerIcon}>
              <Icon name={block.icon} size={16} color={palette.fg} />
            </View>
          )}
          {!!block.title && (
            <Text tag="h2" size='xxxl' weight='bold' color='primary' style={styles.headerTitle}>
              {block.title}
            </Text>
          )}
        </View>
      )}

      {!!block.subtitle && (
        <Text tag="p" size='l' color='primary' style={styles.subtitle}>
          {block.subtitle}
        </Text>
      )}
      {!!block.copy && (
        <Text tag="p" size='m' color='primary' style={styles.text}>
          {block.copy}
        </Text>
      )}

      {(type === 'ordered-list' || type === 'unordered-list') && (
        <PageCopyList block={block} ordered={type === 'ordered-list'} palette={palette} styles={styles} />
      )}
      {type === 'cards' && <PageCopyCards block={block} palette={palette} styles={styles} />}
      {type === 'faq'   && <PageCopyFaq block={block} palette={palette} styles={styles} />}
      {type === 'callout' && (
        <View style={[styles.callout, { borderColor: palette.fg + '33', backgroundColor: palette.bg }]}>
          {!!block.title && (
            <Text tag="h3" size="l" weight="bold" color="primary" style={{ marginBottom: 6 }}>
              {block.title}
            </Text>
          )}
          {!!block.copy && (
            <Text tag="p" size="m" color="primary" style={styles.text}>{block.copy}</Text>
          )}
        </View>
      )}
    </View>
  );
}

function PageCopyList({ block, ordered, palette, styles }) {
  const items = Array.isArray(block.items) ? block.items : [];
  const variant = block.variant || (ordered ? 'number' : 'dot');
  return (
    <View style={styles.list}>
      {items.map((item, itemIndex) => {
        const text = typeof item === 'string'
          ? item
          : (item.title || item.description || item.copy || item.subtitle || '');
        if (!text) return null;
        const isCheck = variant === 'check';
        return (
          <View key={`item-${itemIndex}`} style={styles.listItem}>
            <View style={[styles.marker, styles.markerCheck]}>
              {ordered ? (
                <Text tag="span" size='n' weight='bold' style={{color: palette.fg}}>
                  {itemIndex + 1}
                </Text>
              ) : isCheck ? (
                <Icon name='check' size={13} color={palette.fg} />
              ) : variant === 'dash' ? (
                <Text tag="span" size='n' weight='bold' color='primary'>–</Text>
              ) : (
                <View style={{ width: 5, height: 5, borderRadius: 5, backgroundColor: palette.fg }} />
              )}
            </View>
            <Text tag="p" size='m' color='primary' style={[styles.text, { flex: 1, marginTop: 3 }]}>{text}</Text>
          </View>
        );
      })}
    </View>
  );
}

function PageCopyCards({ block, palette, styles }) {
  const items = Array.isArray(block.items) ? block.items : [];
  return (
    <View style={styles.cardGrid}>
      {items.map((item, itemIndex) => {
        if (!item || typeof item !== 'object') return null;
        return (
          <View key={`card-${itemIndex}`} style={styles.card}>
            {!!item.icon && (
              <View style={styles.cardIcon}>
                <Icon name={item.icon} size={20} color={palette.fg} />
              </View>
            )}
            {!!item.title && <Text tag="h3" size='xl' weight='bold' color='primary'>{item.title}</Text>}
            {!!item.subtitle && (
              <Text tag="p" size='m' weight='bold' color='primary' style={{ lineHeight: 20 }}>{item.subtitle}</Text>
            )}
            {!!(item.description || item.copy) && (
              <Text tag="p" size='m' color='primary' style={{ marginTop: 0, lineHeight: 25 }}>{item.description || item.copy}</Text>
            )}
          </View>
        );
      })}
    </View>
  );
}

function PageCopyFaq({ block, palette, styles }) {
  const items = Array.isArray(block.items) ? block.items : [];
  return (
    <View style={{ gap: 10 }}>
      {items.map((item, itemIndex) => (
        <View key={`faq-${itemIndex}`} style={styles.faqItem}>
          <View style={styles.faqHeader}>
            <Icon name='circle-question' size={14} color={palette.fg} />
            <Text tag="h3" size='l' weight='bold' color='primary' style={styles.faqQ}>
              {item.question || item.q || item.title}
            </Text>
          </View>
          {!!(item.answer || item.a || item.description || item.copy) && (
            <Text tag="p" size='m' color='secondary' style={styles.text}>
              {item.answer || item.a || item.description || item.copy}
            </Text>
          )}
        </View>
      ))}
    </View>
  );
}

function makeStyles(colors, palette, isMD) {
  return StyleSheet.create({
    wrap: {
      gap: 24,
    },
    block: {
      width: '100%',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginTop: 15,
      marginBottom: 15,
    },
    headerIcon: {
      width: 32,
      height: 32,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: palette.bg,
    },
    headerTitle: {
      marginBottom: 0,
      lineHeight: 30,
    },
    subtitle: {
      marginBottom: 20,
      lineHeight: 20,
      maxWidth: 760,
    },
    text: {
      lineHeight: 24,
      maxWidth: 760,
    },
    list: {
      gap: 10,
      marginTop: 8,
    },
    listItem: {
      flexDirection: 'row',
      gap: 10,
      alignItems: 'flex-start',
    },
    marker: {
      width: 26,
      minWidth: 26,
      height: 26,
      borderRadius: 999,
      alignItems: 'center',
      justifyContent: 'center',
      // backgroundColor: colors.solid_color_tertiary,
      marginTop: 1,
    },
    markerCheck: {
      backgroundColor: palette.bg,
    },
    cardGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
      marginTop: 4,
    },
    card: {
      // `calc()` is web-only — RN parses it as invalid and the card either
      // collapses or wraps prematurely. Use a slightly-under-33% basis on
      // native so the 12px parent gap still fits three across.
      flexBasis: isMD
        ? '100%'
        : Platform.select({ web: 'calc(33% - 6px)', default: '32%' }),
      borderWidth: 2,
      borderColor: colors.border_color_secondary,
      borderRadius: 12,
      padding: 16,
      backgroundColor: 'transparent',
      gap: 6,
    },
    cardIcon: {
      width: 42,
      height: 42,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: palette.bg,
      marginBottom: 6,
    },
    faqItem: {
      borderWidth: 2,
      borderColor: colors.border_color_secondary,
      borderRadius: 12,
      padding: 16,
      backgroundColor: 'transparent',
      gap: 8,
    },
    faqHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    faqQ: {
      flex: 1,
      lineHeight: 22,
    },
    callout: {
      borderWidth: 1,
      borderRadius: 12,
      padding: 16,
      marginTop: 8,
      gap: 6,
    },
  });
}

export default React.memo(ToolPageCopy);
